import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, downloadBlob, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Mountain, Download, FileText, Package, MapPin, Satellite, Crosshair, Loader2 } from "lucide-react";

const CARDS = [
  { key: "location", title: "Project Location", icon: MapPin },
  { key: "spectral", title: "Remote Sensing", icon: Satellite },
  { key: "target", title: "Target Areas", icon: Crosshair },
  { key: "structural", title: "Structural / Alteration", icon: Mountain },
];

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/share/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);

  const dl = async (key, path, filename) => {
    setBusy(key);
    try { await downloadBlob(path, filename); toast.success("Download started"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (data === undefined) return <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>;
  if (data === null) return <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500" data-testid="share-invalid">This share link is invalid or has been revoked.</div>;

  const maps = data.maps || {};
  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "IBM Plex Sans, sans-serif" }} data-testid="share-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          {data.logo ? <img src={data.logo} alt="logo" className="h-9 max-w-[140px] object-contain" />
            : <Mountain className="text-orange-500" size={24} />}
          <div className="min-w-0">
            <div className="font-extrabold text-slate-900 tracking-tight truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{data.name}</div>
            <div className="text-[11px] text-slate-400">{data.project_number} · {data.client_name || "-"} · {data.country || "-"}</div>
          </div>
          <div className="ml-auto text-[11px] uppercase tracking-widest text-orange-500 font-semibold hidden sm:block">SYMIN · Client Report</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-900" data-testid="share-snapshot-hero">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/snapshot`}
            alt={`${data.name} summary`} className="w-full object-cover" loading="eager" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Commodity" value={data.commodity} />
          <Stat label="Licence Area" value={`${data.area_km2 ?? "-"} km²`} />
          <Stat label="Targets" value={data.target_count} />
          <Stat label="Prepared By" value={data.consultant || "-"} />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {CARDS.map((c) => (
            <div key={c.key} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`share-map-${c.key}`}>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <c.icon size={16} className="text-orange-500" />
                <div className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{c.title}</div>
              </div>
              <div className="bg-slate-50 aspect-[4/3] overflow-hidden">
                {maps[c.key] ? <img src={maps[c.key]} alt={c.title} className="w-full h-full object-contain" />
                  : <div className="h-full grid place-items-center text-slate-300 text-sm">Not available</div>}
              </div>
              <div className="px-5 py-3">
                <button className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                  onClick={() => dl(c.key, `/share/${token}/map/${c.key}`, `${data.name}_${c.key}.png`)} data-testid={`share-dl-${c.key}`}>
                  <Download size={14} /> Download HD
                </button>
              </div>
            </div>
          ))}
        </div>

        <button data-testid="share-dl-brief" disabled={busy === "brief"}
          onClick={() => dl("brief", `/share/${token}/command-brief.pdf`, `${data.name}_Command_Brief.pdf`)}
          className="w-full flex items-center gap-4 rounded-xl shadow-sm p-5 disabled:opacity-50 hover:opacity-95 transition-opacity"
          style={{ background: "#0B1420" }}>
          <span className="h-11 w-11 rounded-md grid place-items-center shrink-0" style={{ background: "#C20511", color: "#FFFFFF" }}><FileText size={20} /></span>
          <span className="flex-1 text-left">
            <span className="block font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>{busy === "brief" ? "Preparing…" : "Command Brief - 1-page summary"}</span>
            <span className="block text-xs text-slate-300">Colourful executive tiles &amp; portfolio ranking · technical PDF</span>
          </span>
          <Download size={18} className="text-orange-400" />
        </button>

        <div className="grid md:grid-cols-3 gap-4">
          <button data-testid="share-dl-report" disabled={busy === "pdf" || !data.has_report}
            onClick={() => dl("pdf", `/share/${token}/report`, `${data.name}_report.pdf`)}
            className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-orange-400 disabled:opacity-50">
            <span className="h-11 w-11 rounded-md bg-orange-50 text-orange-600 grid place-items-center"><FileText size={20} /></span>
            <span className="flex-1 text-left"><span className="block font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Technical Report</span>
              <span className="block text-xs text-slate-500">Full 9-section PDF</span></span>
            <Download size={18} className="text-slate-400" />
          </button>
          <button data-testid="share-dl-onepager" disabled={busy === "onepager" || !data.has_report}
            onClick={() => dl("onepager", `/share/${token}/onepager`, `${data.name}_onepager.pdf`)}
            className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-orange-400 disabled:opacity-50">
            <span className="h-11 w-11 rounded-md bg-orange-50 text-orange-600 grid place-items-center"><FileText size={20} /></span>
            <span className="flex-1 text-left"><span className="block font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Executive One-Pager</span>
              <span className="block text-xs text-slate-500">1-page technical summary</span></span>
            <Download size={18} className="text-slate-400" />
          </button>
          <button data-testid="share-dl-zip" disabled={busy === "zip"}
            onClick={() => dl("zip", `/share/${token}/zip`, `${data.name}_package.zip`)}
            className="flex items-center gap-4 bg-slate-900 rounded-xl shadow-sm p-5 hover:bg-slate-800 disabled:opacity-50">
            <span className="h-11 w-11 rounded-md bg-orange-500 text-white grid place-items-center"><Package size={20} /></span>
            <span className="flex-1 text-left"><span className="block font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>{busy === "zip" ? "Preparing…" : "Download All (ZIP)"}</span>
              <span className="block text-xs text-slate-300">Maps, KML, CSV & report</span></span>
            <Download size={18} className="text-orange-400" />
          </button>
        </div>

        <p className="text-[11px] text-slate-400 text-center pb-6">CONFIDENTIAL - Exploration targets requiring field verification; not a statement of Mineral Resources or Reserves.</p>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
    <div className="text-slate-900 font-bold mt-1 truncate">{value}</div>
  </div>
);
